/**
 * L'écran d'administration des ARTICLES.
 *
 * *Demandes de Chris du 20/08/2026 : « un menu article séparé des ressources génériques »,
 * « je veux pouvoir modifier ajouter ou supprimer une ligne », « exporter tout le fichier dans
 * excel puis le réimporter », « générer un qr code sur chaque article », « rajouter deux photos
 * sur chaque article », « respecte bien la structure du fichier avec toutes les colonnes », puis
 * — devant la première version — « il n'y a pas toutes les colonnes de mon fichier ? ».*
 *
 * ## Le tableau porte TOUTES les colonnes du fichier, dans l'ordre du fichier
 *
 * La première version n'en montrait que six et rangeait les vingt-et-une autres dans une fiche,
 * par souci de lisibilité. C'était une erreur de lecture : ce référentiel **est** le classeur de
 * l'ERP, et celui qui l'administre le relit en balayant ses colonnes, pas en ouvrant une fiche
 * par ligne. L'écran, l'export et le classeur d'origine présentent donc exactement les mêmes
 * colonnes, dans le même ordre — c'est aussi ce qui permet de comparer l'écran au fichier sans
 * traduire.
 *
 * Le tableau défile horizontalement, et **la colonne du code reste collée à gauche** : sans
 * elle, on ne sait plus quelle ligne on modifie dès la dixième colonne.
 *
 * ## Le QR code s'agrandit en plein écran
 *
 * *« Je ne peux pas prendre le qr code trop petit il faudrait pouvoir l'agrandir quand on clique
 * dessus »* (Chris, 20/08/2026). Un clic sur le QR d'une ligne ouvre un calque où il fait
 * plusieurs centimètres de côté — la taille qu'une douchette et un appareil photo de téléphone
 * savent lire. Ni la vignette du tableau ni le QR de la fiche n'y suffisaient, et l'agrandir sur
 * place aurait crevé une ligne de vingt-huit colonnes.
 *
 * Ne restent dans la fiche que les deux photos, qui n'entrent pas dans une cellule. Tout le reste
 * se modifie directement dans le tableau.
 *
 * ## Trois partis pris, les mêmes que les autres référentiels
 *
 *  - **le code ne se modifie pas.** Il s'affiche, il ne s'édite pas : c'est par lui qu'une ligne
 *    de contre-étude retrouvera son article, et par lui que le classeur se réimporte. Pour
 *    corriger un code, on en crée un autre et on supprime l'ancien — ce qui se voit ;
 *  - **un champ vidé est vidé**, à l'écran comme dans le classeur. La règle du 20/08 vaut des
 *    deux côtés : deux comportements différents selon qu'on saisit ou qu'on importe serait la
 *    garantie d'un malentendu ;
 *  - **le classeur exporté EST le format attendu au retour.** C'est la seule façon d'être sûr
 *    que ce qui sort rentre, et c'est vérifié sur le fichier réel.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";
import {
  ADRESSE_CLASSEUR_ARTICLES,
  adresseEncodeeDansLeQr,
  adresseEtiquettesArticles,
  adressePhotoArticle,
  adresseQrArticle,
  chargerArticles,
  creerArticle,
  deposerPhotoArticle,
  importerArticles,
  modifierArticle,
  retirerPhotoArticle,
  supprimerArticle,
} from "./api";
import type { Article, BilanImportArticles } from "./api";

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; articles: readonly Article[]; baseQr: string }
  | { phase: "erreur"; raison: string };

type Genre = "texte" | "nombre" | "booleen";

/**
 * Les colonnes du classeur de l'ERP, **dans son ordre et sous ses en-têtes**.
 *
 * Les libellés sont ceux du fichier, à la lettre — « Libelle Code 1 » sans accent y compris :
 * l'écran doit se lire en face du classeur, et un en-tête corrigé au passage obligerait à
 * traduire pour retrouver sa colonne. Ce sont aussi ceux que l'export écrit et que le réimport
 * attend, côté serveur.
 *
 * Le code (« N° ») n'y figure pas : il a sa propre colonne, figée à gauche et non modifiable.
 */
const COLONNES_FICHIER: readonly {
  readonly champ: keyof Article;
  readonly entete: string;
  readonly genre: Genre;
  /**
   * La largeur de la colonne, en caractères.
   *
   * *Chris, 20/08/2026 : « ajuste également la largeur des colonnes de la liste à la longueur des
   * libellés ».* Chaque valeur est **mesurée sur le classeur réel** — le plus long contenu de la
   * colonne, ou son en-tête s'il est plus long, borné pour qu'un article sur trois mille ne prenne
   * pas la moitié du tableau à lui seul.
   *
   * Une largeur uniforme donnait six centimètres à « Code 1 » comme à « Description » : les
   * colonnes vides occupaient autant que celles qu'on lit, et il fallait défiler pour rien.
   *
   * *Chris, 27/08/2026 : « colonnes à ajuster car tableau illisible ».* Ces largeurs étaient
   * posées sur le CHAMP DE SAISIE de la cellule, et la feuille de style le borne à la largeur de
   * sa colonne (`max-width: 100%`). Comme le tableau est en `table-layout: fixed` sur
   * `width: 100%`, les trente colonnes se partageaient la fenêtre à parts égales : aucune de ces
   * valeurs n'a jamais pris effet. Elles vivent maintenant dans un `colgroup`, la seule chose
   * que cette mise en page regarde — le champ de saisie remplit alors exactement sa colonne.
   *
   * Les dix-neuf premières sont **remesurées le 27/08/2026 sur les 3 247 lignes du classeur**.
   * Les huit colonnes de classification (`Code 1` à `Libelle Code 4`) gardent leurs valeurs :
   * le classeur d'origine ne porte pas ces colonnes, il n'y avait donc rien à mesurer.
   */
  readonly largeur: number;
}[] = [
  { champ: "description", entete: "Description", genre: "texte", largeur: 44 },
  { champ: "uniteBase", entete: "Unité de base", genre: "texte", largeur: 8 },
  { champ: "codeFamille", entete: "Code Famille", genre: "texte", largeur: 17 },
  { champ: "coutUnitaire", entete: "Coût unitaire", genre: "nombre", largeur: 11 },
  { champ: "coutEtude", entete: "Coût étude", genre: "nombre", largeur: 9 },
  { champ: "prixUnitaire", entete: "Prix unitaire", genre: "nombre", largeur: 10 },
  { champ: "procedureAppro", entete: "Procédure d'appro.", genre: "texte", largeur: 11 },
  { champ: "fournisseurNumero", entete: "N° fournisseur", genre: "texte", largeur: 11 },
  { champ: "fournisseurNom", entete: "Nom fournisseur", genre: "texte", largeur: 18 },
  { champ: "categorieDemandePrix", entete: "Catégorie demande de prix", genre: "texte", largeur: 12 },
  { champ: "codeCategorieArticle", entete: "Code catégorie article", genre: "texte", largeur: 11 },
  { champ: "stocks", entete: "Stocks", genre: "nombre", largeur: 11 },
  { champ: "bloque", entete: "Bloqué", genre: "booleen", largeur: 6 },
  { champ: "centreCode", entete: "Centre Code", genre: "texte", largeur: 8 },
  { champ: "activiteCode", entete: "Activité Code", genre: "texte", largeur: 8 },
  { champ: "natureCode", entete: "Nature Code", genre: "texte", largeur: 11 },
  { champ: "description2", entete: "Description 2", genre: "texte", largeur: 20 },
  { champ: "modeleEchelonnement", entete: "Modèle échelonnement par défaut", genre: "texte", largeur: 13 },
  // La colonne QR du fichier vient ici. Elle est vide dans le classeur d'origine : c'est
  // l'application qui la produit, et elle s'affiche en image plutôt qu'en texte.
  { champ: "code1", entete: "Code 1", genre: "texte", largeur: 7 },
  { champ: "libelleCode1", entete: "Libelle Code 1", genre: "texte", largeur: 32 },
  { champ: "code2", entete: "Code 2", genre: "texte", largeur: 7 },
  { champ: "libelleCode2", entete: "Libelle Code 2", genre: "texte", largeur: 32 },
  { champ: "code3", entete: "Code 3", genre: "texte", largeur: 7 },
  { champ: "libelleCode3", entete: "Libelle Code 3", genre: "texte", largeur: 32 },
  { champ: "code4", entete: "Code 4", genre: "texte", largeur: 7 },
  { champ: "libelleCode4", entete: "Libelle Code 4", genre: "texte", largeur: 32 },
];

/**
 * Les mêmes champs, groupés pour la FICHE.
 *
 * *Chris, 20/08/2026 : « cela ne me met pas toutes les infos de la fiche ? je vois juste un
 * code ».* Deux lectures d'une même donnée, et elles ne servent pas au même geste :
 *
 *  - **le tableau** est le classeur de l'ERP — colonnes dans l'ordre du fichier, pour balayer
 *    deux mille lignes et les comparer à l'Excel ;
 *  - **la fiche** est l'article — ce qu'on regarde quand on arrive dessus, en particulier après
 *    avoir scané son étiquette. L'ordre d'un export n'y veut rien dire : ce qu'on cherche, c'est
 *    de quoi il s'agit, ce qu'il coûte, d'où il vient, à quoi il est imputé, comment il est
 *    classé.
 *
 * Les deux se modifient. Ce n'est pas une duplication de règles : la même fonction écrit dans les
 * deux cas, seul l'agencement change.
 */
const GROUPES: readonly { readonly titre: string; readonly champs: readonly (keyof Article)[] }[] = [
  { titre: "Identification", champs: ["description", "description2", "uniteBase", "codeFamille"] },
  { titre: "Montants", champs: ["coutUnitaire", "coutEtude", "prixUnitaire"] },
  {
    titre: "Approvisionnement",
    champs: [
      "procedureAppro",
      "fournisseurNumero",
      "fournisseurNom",
      "categorieDemandePrix",
      "codeCategorieArticle",
      "stocks",
      "bloque",
      "modeleEchelonnement",
    ],
  },
  { titre: "Imputation", champs: ["centreCode", "activiteCode", "natureCode"] },
  {
    titre: "Classification",
    champs: [
      "code1",
      "libelleCode1",
      "code2",
      "libelleCode2",
      "code3",
      "libelleCode3",
      "code4",
      "libelleCode4",
    ],
  },
];

/** La colonne d'un champ : son en-tête et son genre, retrouvés une fois pour la fiche. */
function colonneDe(champ: keyof Article): (typeof COLONNES_FICHIER)[number] | undefined {
  return COLONNES_FICHIER.find((c) => c.champ === champ);
}

/** Où la colonne QR se glisse : à sa place dans le fichier, après le modèle d'échelonnement. */
const RANG_DU_QR = COLONNES_FICHIER.findIndex((c) => c.champ === "code1");

/**
 * La grille du tableau, posée en `colgroup`.
 *
 * C'est la seule façon de fixer une largeur ici : le tableau est en `table-layout: fixed`
 * (idiome de `table.ressources`), et cette mise en page ne lit les largeurs que sur la première
 * rangée ou sur le `colgroup`. Une largeur portée par le champ de saisie est rabattue sur la
 * colonne par `max-width: 100%`, donc elle ne fait rien.
 *
 * L'ordre suit celui du `thead`, colonne pour colonne : le code, les champs jusqu'au QR, le QR,
 * les champs suivants, les photos, la suppression. `0.7rem` s'ajoute à chaque largeur de texte —
 * en `fixed`, la largeur d'un `<col>` est celle de la BORDURE de la colonne, et les deux
 * `0.35rem` de marge intérieure des cellules mangeraient autant de caractères que ce qu'on a
 * mesuré.
 */
function ColonnesDuClasseur(): JSX.Element {
  const enCaracteres = (largeur: number): string => `calc(${String(largeur)}ch + 0.7rem)`;
  return (
    <colgroup>
      <col style={{ width: enCaracteres(14) }} />
      {/* Stock magasin : hors classeur, juste après le code (09/09/2026). */}
      <col style={{ width: enCaracteres(12) }} />
      {COLONNES_FICHIER.slice(0, RANG_DU_QR).map((c) => (
        <col key={c.champ} style={{ width: enCaracteres(c.largeur) }} />
      ))}
      <col style={{ width: "3.2rem" }} />
      {COLONNES_FICHIER.slice(RANG_DU_QR).map((c) => (
        <col key={c.champ} style={{ width: enCaracteres(c.largeur) }} />
      ))}
      <col style={{ width: "6.4rem" }} />
      <col style={{ width: enCaracteres(9) }} />
    </colgroup>
  );
}

/**
 * Combien de lignes s'affichent avant qu'on demande d'affiner.
 *
 * Cent et non deux mille : chaque ligne porte vingt-six champs de saisie, donc deux mille lignes
 * feraient cinquante mille champs dans la page — le navigateur tiendrait, la frappe non.
 */
const LIGNES_AFFICHEES = 100;

/** Sans accents ni casse : on cherche « reduction » et on trouve « RÉDUCTION ». */
function normaliser(texte: string): string {
  return texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/gu, "")
    .toLowerCase();
}

/**
 * Les articles qui répondent à une recherche : par code OU par libellé.
 *
 * Les deux, et non l'un ou l'autre selon la forme de la saisie : on cherche « 1000001 » quand on
 * a la référence sous les yeux, « réduction » quand on ne l'a pas. Deviner laquelle des deux
 * l'utilisateur veut dire aurait fait taire l'écran une fois sur deux.
 *
 * Chaque mot doit se retrouver, dans le code ou dans le libellé, mais pas forcément dans le même
 * ni dans l'ordre : « reduction 110 » trouve « RÉDUCTION A JOINTS MF Ø 110/100 MM » — c'est
 * ainsi qu'on cherche quand on ne sait plus le libellé exact.
 */
export function chercherArticles(
  articles: readonly Article[],
  recherche: string,
): readonly Article[] {
  const mots = normaliser(recherche).split(/\s+/u).filter((m) => m !== "");
  if (mots.length === 0) return articles;
  return articles.filter((a) => {
    const dans = `${normaliser(a.code)} ${normaliser(a.description)}`;
    return mots.every((mot) => dans.includes(mot));
  });
}

/* ---------------------------------------------------------------------------
 * Le filtre par colonne et la saisie assistée (09/09/2026)
 * ------------------------------------------------------------------------- */

/**
 * Les colonnes dont les valeurs forment un ensemble FERMÉ, et le libellé qui accompagne le code.
 *
 * *Demande de Chris, 09/09/2026 : « pour les colonnes Unité de base, centre code, activité code,
 * Nature code, procédure d'appro et bloqué — idem pour toutes les colonnes classification même si
 * elles doivent être remplies automatiquement avec le code famille — il faudrait avoir un filtre
 * et une saisie assistée avec les libellés des champs de la colonne ».*
 *
 * **Aucune liste n'est écrite en dur.** Elles se lisent dans le référentiel déjà chargé : c'est
 * la même règle que le préfixe `100` des articles ou les fonctions à nommer — une liste de codes
 * d'ERP dans le code du produit est un fork qui commence, et elle serait périmée au premier
 * classeur réimporté. Mesuré ce jour sur la production : 2 valeurs d'unité de base, 1 centre,
 * 2 activités, 6 natures, 1 procédure, 272 familles, et de 5 à 243 codes de classification.
 *
 * Le libellé vient de la colonne voisine, quand elle existe — c'est ce qui rend « 005 » lisible.
 */
const LIBELLE_DE: Partial<Record<keyof Article, keyof Article>> = {
  code1: "libelleCode1",
  code2: "libelleCode2",
  code3: "libelleCode3",
  code4: "libelleCode4",
};

/** Au-delà, une liste déroulante cesse d'aider : le filtre redevient une saisie libre. */
const MAX_LISTE_DEROULANTE = 40;

/** L'identifiant du `<datalist>` d'une colonne. Un seul par colonne, partagé par ses cellules. */
function idDeLaListe(champ: keyof Article): string {
  return `articles-valeurs-${champ}`;
}

/**
 * Ce qu'un en-tête ne peut pas dire, et qui a coûté une question.
 *
 * *« Pourquoi la quantité entrée en stock n'a pas été reprise ? »* (Chris, 09/09/2026). Elle
 * l'avait été. Deux colonnes portent un stock et une seule était visible, sous le nom « Stocks » —
 * celui du classeur, qu'on ne peut pas changer sans casser le va-et-vient Excel. L'infobulle est
 * donc le seul endroit où lever l'ambiguïté sans toucher au format du fichier.
 */
const INFOBULLES: Partial<Record<keyof Article, string>> = {
  stocks:
    "Colonne « Stocks » DU CLASSEUR DE L'ERP : elle est écrasée à chaque réimport et reste vide " +
    "sur un article créé ici. Le stock que l'application tient est « Stock magasin », à gauche.",
  codeFamille:
    "Renseigner la famille remplit seule les huit colonnes de classification, quand la table " +
    "porte déjà un article de cette famille.",
};

/** Une valeur proposée : ce qui s'écrit dans la cellule, et ce qui se lit à côté. */
type Proposition = { readonly valeur: string; readonly libelle: string | null };

/**
 * Les valeurs présentes dans une colonne, dédoublonnées et rangées.
 *
 * Rangées par valeur et non par fréquence : on cherche un code qu'on connaît à moitié, et une
 * liste triée se parcourt. Le tri est celui du français (`localeCompare`) pour que « É » ne
 * tombe pas après « Z ».
 */
function propositionsDeLaColonne(
  articles: readonly Article[],
  champ: keyof Article,
): readonly Proposition[] {
  const champLibelle = LIBELLE_DE[champ];
  const par = new Map<string, string | null>();
  for (const a of articles) {
    const brut = a[champ];
    if (typeof brut !== "string" || brut.trim() === "") continue;
    if (par.has(brut)) continue;
    const libelle = champLibelle === undefined ? null : a[champLibelle];
    par.set(brut, typeof libelle === "string" && libelle !== "" ? libelle : null);
  }
  return [...par.entries()]
    .map(([valeur, libelle]) => ({ valeur, libelle }))
    .sort((x, y) => x.valeur.localeCompare(y.valeur, "fr"));
}

/**
 * Ce que le filtre d'une colonne retient.
 *
 * `""` = aucun filtre. Sur une colonne booléenne, `"oui"` et `"non"` — et non `true`/`false` :
 * un filtre se lit dans la langue de l'écran.
 */
type Filtres = Partial<Record<keyof Article, string>>;

/**
 * Les articles qui passent tous les filtres de colonne.
 *
 * **Tous, et non l'un d'eux** : poser deux filtres, c'est croiser deux questions. Une liste
 * déroulante demande la valeur EXACTE — c'est une valeur choisie dans la colonne, pas une
 * recherche —, une saisie libre demande « contient », parce qu'on tape trois lettres d'un libellé
 * dont on ne connaît pas la ponctuation.
 */
function filtrer(
  articles: readonly Article[],
  filtres: Filtres,
  exacts: ReadonlySet<keyof Article>,
): readonly Article[] {
  const poses = Object.entries(filtres).filter(([, v]) => v !== undefined && v !== "");
  if (poses.length === 0) return articles;
  return articles.filter((a) =>
    poses.every(([champ, attendu]) => {
      const valeur = a[champ as keyof Article];
      if (typeof valeur === "boolean") return (valeur ? "oui" : "non") === attendu;
      const texte = typeof valeur === "string" ? valeur : "";
      return exacts.has(champ as keyof Article)
        ? texte === attendu
        : normaliser(texte).includes(normaliser(attendu as string));
    }),
  );
}

/**
 * Le filtre d'une colonne : une liste déroulante quand les valeurs se comptent, une saisie sinon.
 *
 * La liste déroulante montre le **libellé à côté du code** quand la colonne en a un — « 005 —
 * Réductions PVC » : c'est ce que Chris demande, et un code seul ne se choisit pas de mémoire.
 * Au-delà de quarante valeurs, dérouler devient plus long que taper : le filtre passe alors en
 * « contient », insensible aux accents et à la casse comme la recherche.
 *
 * Une colonne sans aucune valeur renseignée n'affiche pas de filtre du tout — proposer de filtrer
 * sur du vide n'apporte rien et ajoute une case à lire sur trente colonnes.
 */
function FiltreDeColonne({
  champ,
  entete,
  genre,
  propositions,
  valeur,
  onFiltrer,
}: {
  champ: keyof Article;
  entete: string;
  genre: Genre;
  propositions: readonly Proposition[];
  valeur: string;
  onFiltrer: (valeur: string) => void;
}): JSX.Element | null {
  if (genre === "booleen") {
    return (
      <select
        className="saisie articles__filtre"
        aria-label={`Filtrer sur ${entete}`}
        value={valeur}
        onChange={(e) => {
          onFiltrer(e.target.value);
        }}
      >
        <option value="">tous</option>
        <option value="oui">oui</option>
        <option value="non">non</option>
      </select>
    );
  }

  /*
    Pas de filtre sur un MONTANT (mesuré à l'écran le 09/09/2026).

    Un filtre « contient » sur « Coût unitaire » proposait de chercher les prix qui contiennent
    « 15 » — ce qui attrape 15, 150 et 1,15. Personne ne cherche un article par son prix exact, et
    quatre cases inutiles sur une ligne de trente colonnes font douter des cases qui servent. Une
    fourchette de prix serait autre chose : elle se demandera si le besoin apparaît.
  */
  if (genre === "nombre") return null;

  if (propositions.length === 0) return null;

  if (propositions.length <= MAX_LISTE_DEROULANTE) {
    return (
      <select
        className="saisie articles__filtre"
        aria-label={`Filtrer sur ${entete}`}
        value={valeur}
        onChange={(e) => {
          onFiltrer(e.target.value);
        }}
      >
        <option value="">tous</option>
        {propositions.map((p) => (
          <option key={p.valeur} value={p.valeur}>
            {p.libelle === null ? p.valeur : `${p.valeur} — ${p.libelle}`}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      className="saisie saisie--texte articles__filtre"
      aria-label={`Filtrer sur ${entete}`}
      // La même liste sert au filtre et à la cellule : une valeur qu'on peut choisir dans une
      // cellule doit pouvoir se choisir dans son filtre.
      list={idDeLaListe(champ)}
      value={valeur}
      placeholder="filtrer…"
      onChange={(e) => {
        onFiltrer(e.target.value);
      }}
    />
  );
}

/** Un champ qui n'envoie sa valeur qu'une fois la saisie terminée. */
function Champ({
  valeur,
  aligne,
  largeur,
  enAttente,
  liste,
  onValider,
}: {
  valeur: string;
  aligne?: "droite";
  largeur?: number;
  enAttente: boolean;
  /** L'identifiant d'un `<datalist>` : le navigateur propose alors les valeurs de la colonne. */
  liste?: string;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie${aligne === "droite" ? "" : " saisie--texte"}${enAttente ? " saisie--attente" : ""}`}
      // La largeur est portée par le champ et non par la colonne : c'est le champ qui déborde,
      // et une largeur posée sur la colonne serait ignorée dès qu'un contenu la dépasse.
      style={largeur === undefined ? undefined : { width: `${String(largeur)}ch` }}
      /*
        La saisie assistée est un `datalist` et NON une liste déroulante (09/09/2026).

        La cellule reste un champ libre : les valeurs proposées sont celles que la colonne porte
        aujourd'hui, pas une liste fermée. Un `select` aurait interdit la première occurrence
        d'une valeur neuve — un nouveau code de nature, une unité qui n'existe pas encore — et
        aurait donc fait passer par le classeur ce que l'écran est censé permettre.
      */
      list={liste}
      value={brouillon ?? valeur}
      placeholder="—"
      disabled={enAttente}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Une cellule du tableau, selon le genre de sa colonne. */
function Cellule({
  article,
  champ,
  genre,
  largeur,
  enAttente,
  liste,
  onModifier,
}: {
  article: Article;
  champ: keyof Article;
  genre: Genre;
  largeur: number;
  enAttente: boolean;
  /** Le `datalist` des valeurs de la colonne, quand elle en a un. */
  liste?: string;
  onModifier: (champs: Record<string, unknown>) => void;
}): JSX.Element {
  const valeur = article[champ];

  if (genre === "booleen") {
    return (
      <input
        type="checkbox"
        checked={valeur === true}
        disabled={enAttente}
        onChange={(e) => {
          onModifier({ [champ]: e.target.checked });
        }}
      />
    );
  }

  return (
    <Champ
      valeur={typeof valeur === "string" ? valeur : ""}
      aligne={genre === "nombre" ? "droite" : undefined}
      largeur={largeur}
      enAttente={enAttente}
      liste={liste}
      onValider={(saisi) => {
        onModifier({ [champ]: saisi });
      }}
    />
  );
}

/**
 * Une photo dans le tableau : la vignette si elle est là, un bouton de dépôt sinon.
 *
 * *Chris, 20/08/2026 : « comment j'intègre les deux photos ».* Elles étaient dans la fiche, qu'on
 * n'ouvre qu'en cliquant un code — donc invisibles. Elles sont maintenant **sur la ligne**, à
 * portée de clic, et la fiche ne fait plus que les montrer en grand.
 *
 * `capture="environment"` ouvre directement l'appareil photo arrière sur un téléphone, sans
 * passer par la galerie : c'est le geste du magasinier devant son rayonnage. Sur un ordinateur,
 * l'attribut est ignoré et le sélecteur de fichiers s'ouvre normalement.
 */
function PhotoDeLigne({
  article,
  numero,
  enAttente,
  onDeposer,
}: {
  article: Article;
  numero: 1 | 2;
  enAttente: boolean;
  onDeposer: (fichier: File) => void;
}): JSX.Element {
  const champFichier = useRef<HTMLInputElement>(null);
  const presente = numero === 1 ? article.photo1 : article.photo2;

  return (
    <span className="articles__photo">
      {presente ? (
        <a href={adressePhotoArticle(article, numero)} target="_blank" rel="noreferrer">
          <img
            src={adressePhotoArticle(article, numero)}
            alt={`Photo ${String(numero)} de ${article.code}`}
            loading="lazy"
          />
        </a>
      ) : (
        <button
          type="button"
          className="articles__photo-vide"
          title={`Déposer la photo ${String(numero)}`}
          disabled={enAttente}
          onClick={() => champFichier.current?.click()}
        >
          +
        </button>
      )}
      <input
        ref={champFichier}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        hidden
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          e.target.value = "";
          if (fichier !== undefined) onDeposer(fichier);
        }}
      />
    </span>
  );
}

/** Un emplacement de photo : l'image si elle est là, le bouton de dépôt sinon. */
function Photo({
  article,
  numero,
  enAttente,
  onDeposer,
  onRetirer,
}: {
  article: Article;
  numero: 1 | 2;
  enAttente: boolean;
  onDeposer: (fichier: File) => void;
  onRetirer: () => void;
}): JSX.Element {
  const champFichier = useRef<HTMLInputElement>(null);
  const presente = numero === 1 ? article.photo1 : article.photo2;

  return (
    <div className="article-photo">
      {presente ? (
        <a href={adressePhotoArticle(article, numero)} target="_blank" rel="noreferrer">
          <img
            src={adressePhotoArticle(article, numero)}
            alt={`Photo ${String(numero)} de ${article.code}`}
          />
        </a>
      ) : (
        <div className="article-photo__vide">Photo {numero}</div>
      )}
      <p className="etat">
        <button
          type="button"
          className="lien"
          disabled={enAttente}
          onClick={() => champFichier.current?.click()}
        >
          {presente ? "Remplacer" : "Déposer"}
        </button>
        {presente && (
          <>
            {" · "}
            <button type="button" className="lien" disabled={enAttente} onClick={onRetirer}>
              Retirer
            </button>
          </>
        )}
      </p>
      <input
        ref={champFichier}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          e.target.value = "";
          if (fichier !== undefined) onDeposer(fichier);
        }}
      />
    </div>
  );
}

/**
 * La fiche d'un article : son QR code en grand, ses deux photos.
 *
 * Les champs, eux, se modifient dans le tableau — ils y sont tous. Ne restent ici que les deux
 * choses qui n'entrent pas dans une cellule.
 */
/**
 * Le QR code en GRAND, par-dessus l'écran.
 *
 * *Chris, 20/08/2026 : « je ne peux pas prendre le qr code trop petit il faudrait pouvoir
 * l'agrandir quand on clique dessus ».* La vignette du tableau et même le QR de la fiche sont
 * trop petits pour être lus depuis l'écran : une douchette et un appareil photo de téléphone ont
 * besoin de quelques centimètres de côté.
 *
 * D'où un calque plein écran plutôt qu'un agrandissement sur place : dans un tableau de
 * vingt-huit colonnes, il n'y a aucune place pour un carré de dix centimètres, et un QR qui
 * pousserait ses voisines rendrait la ligne illisible pendant qu'on le scane.
 *
 * Le côté est en `vmin` — le plus petit des deux côtés de la fenêtre — pour que le carré reste
 * entier quelle que soit la forme de l'écran, et que rien ne le rogne : un QR tronqué ne se lit
 * pas, et rien à l'écran ne dirait pourquoi.
 */
function QrAgrandi({
  article,
  baseQr,
  onFermer,
}: {
  article: Article;
  baseQr: string;
  onFermer: () => void;
}): JSX.Element {
  // La touche d'échappement autant que le clic : on tient un téléphone d'une main et la souris
  // n'est pas toujours là.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onFermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => {
      window.removeEventListener("keydown", surTouche);
    };
  }, [onFermer]);

  return (
    <div
      className="qr-calque"
      role="dialog"
      aria-modal="true"
      aria-label={`QR code de ${article.code}`}
      onClick={onFermer}
    >
      {/*
        Le clic sur le CADRE ne referme pas : on approche le téléphone de l'écran, et un doigt
        posé par mégarde ferait disparaître le code qu'on essaie de lire. Seul le fond referme.
      */}
      <div
        className="qr-calque__cadre"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <img
          className="qr-calque__image"
          src={adresseQrArticle(article.id, baseQr)}
          alt={`QR code de ${article.code}`}
        />
        <p className="qr-calque__code">{article.code}</p>
        <p className="qr-calque__libelle">{article.description}</p>
        <p className="qr-calque__contenu">{adresseEncodeeDansLeQr(baseQr, article.code)}</p>
        <p className="etat">
          <a href={adresseEtiquettesArticles([article.code])} target="_blank" rel="noreferrer">
            Imprimer l'étiquette 6 × 6 cm
          </a>
          {" · "}
          <button type="button" className="lien" onClick={onFermer}>
            Fermer
          </button>
        </p>
      </div>
    </div>
  );
}

function Fiche({
  article,
  baseQr,
  enAttente,
  onAgrandirQr,
  onModifier,
  onDeposerPhoto,
  onRetirerPhoto,
  onFermer,
}: {
  article: Article;
  baseQr: string;
  enAttente: boolean;
  onAgrandirQr: () => void;
  onModifier: (champs: Record<string, unknown>) => void;
  onDeposerPhoto: (numero: 1 | 2, fichier: File) => void;
  onRetirerPhoto: (numero: 1 | 2) => void;
  onFermer: () => void;
}): JSX.Element {
  return (
    <section className="article-fiche">
      <h3>
        <span className="code">{article.code}</span> — {article.description}
        {" · "}
        <button type="button" className="lien" onClick={onFermer}>
          fermer
        </button>
      </h3>

      <div className="article-fiche__corps">
        <div className="article-fiche__champs">
          {GROUPES.map((groupe) => (
            <div className="article-groupe" key={groupe.titre}>
              <h4>{groupe.titre}</h4>
              <table className="article-champs">
                <tbody>
                  {groupe.champs.map((champ) => {
                    const colonne = colonneDe(champ);
                    if (colonne === undefined) return null;
                    return (
                      <tr key={champ}>
                        <th>{colonne.entete}</th>
                        <td>
                          <Cellule
                            article={article}
                            champ={champ}
                            genre={colonne.genre}
                            largeur={Math.min(colonne.largeur, 26)}
                            enAttente={enAttente}
                            onModifier={onModifier}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="article-groupe">
          <h4>QR code</h4>
          {/*
            Le QR encode l'ADRESSE de cette fiche : scané, il l'ouvre. Un clic dessus l'agrandit
            à la taille qu'une douchette lit.
          */}
          <button
            type="button"
            className="article-qr"
            title="Agrandir pour le scaner"
            onClick={onAgrandirQr}
          >
            <img src={adresseQrArticle(article.id, baseQr)} alt={`QR code de ${article.code}`} />
          </button>
          {/*
            **Ce que le QR contient, écrit en clair.** Sans cela, personne ne peut vérifier ce
            qu'une étiquette portera avant d'en imprimer deux mille — et une installation dont
            `APP_URL` est resté sur sa valeur par défaut fabriquerait des étiquettes vers
            `localhost` sans que rien ne le signale.
          */}
          <p className="article-qr__contenu">{adresseEncodeeDansLeQr(baseQr, article.code)}</p>
          <p className="etat">
            <button type="button" className="lien" onClick={onAgrandirQr}>
              Agrandir pour scaner
            </button>
            {" · "}
            <a href={adresseEtiquettesArticles([article.code])} target="_blank" rel="noreferrer">
              Imprimer son étiquette
            </a>
          </p>
          <p className="note">
            Étiquette de 6 × 6 cm — imprimer à 100 %, sans mise à l'échelle. Scanner ce code ouvre
            la fiche de l'article.
          </p>
        </div>

        <div className="article-groupe">
          <h4>Photos</h4>
          <div className="article-photos">
            {([1, 2] as const).map((numero) => (
              <Photo
                key={numero}
                article={article}
                numero={numero}
                enAttente={enAttente}
                onDeposer={(fichier) => {
                  onDeposerPhoto(numero, fichier);
                }}
                onRetirer={() => {
                  onRetirerPhoto(numero);
                }}
              />
            ))}
          </div>
          <p className="note">JPEG, PNG ou WebP, 5 Mo au plus par photo.</p>
        </div>
      </div>
    </section>
  );
}

function Ajout({
  onAjouter,
}: {
  onAjouter: (code: string, description: string) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const complet = code.trim() !== "" && description.trim() !== "";

  return (
    <form
      className="ajout"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complet) return;
        onAjouter(code.trim(), description.trim());
        setCode("");
        setDescription("");
      }}
    >
      <input
        placeholder="Code article"
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
        }}
        aria-label="Code du nouvel article"
      />
      <input
        placeholder="Description"
        className="ajout__libelle"
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        aria-label="Description du nouvel article"
      />
      <button type="submit" disabled={!complet}>
        Ajouter
      </button>
    </form>
  );
}

/** Ce que le réimport a fait, dit en une phrase que personne n'a à interpréter. */
function Bilan({ bilan }: { bilan: BilanImportArticles }): JSX.Element {
  return (
    <div className="etat">
      <strong>
        {bilan.ajoutes} ajouté(s), {bilan.modifies} modifié(s), {bilan.inchanges} inchangé(s).
      </strong>{" "}
      Un article absent du classeur n'est jamais supprimé — pour en retirer un, le supprimer ici.
      En revanche, <strong>une cellule vidée ou mise à zéro vide le champ</strong>.
      {bilan.colonnesAbsentes.length > 0 && (
        <p className="note">
          Colonnes absentes du classeur, donc laissées intactes :{" "}
          {bilan.colonnesAbsentes.join(", ")}.
        </p>
      )}
      {bilan.refuses.length > 0 && (
        <ul className="alerte">
          {bilan.refuses.slice(0, 50).map((r) => (
            <li key={`${String(r.ligne)}-${r.raison}`}>
              ligne {r.ligne} du classeur : {r.raison}
            </li>
          ))}
          {bilan.refuses.length > 50 && <li>… et {bilan.refuses.length - 50} autre(s).</li>}
        </ul>
      )}
    </div>
  );
}

/**
 * @param codeInitial Le code demandé par l'adresse — `/article/1000001`, ce que le QR encode.
 *   L'écran s'ouvre alors sur cet article : la recherche est préremplie et sa fiche est ouverte.
 *   C'est là qu'atterrit un scan, et c'est le seul endroit du produit dont l'adresse désigne une
 *   ligne — d'où la lecture directe de `window.location` plutôt qu'un routeur.
 */
export function Articles({ codeInitial }: { codeInitial?: string } = {}): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [recherche, setRecherche] = useState(codeInitial ?? "");
  const [ouvert, setOuvert] = useState<string | null>(null);
  /** L'article dont le QR est affiché en grand, par-dessus l'écran. */
  const [qrEnGrand, setQrEnGrand] = useState<string | null>(null);
  /**
   * On arrive d'un scan : l'écran ne montre QUE cet article.
   *
   * *Chris, 20/08/2026 : « quand je prends en photo le qr code, cela ne me montre pas la fiche
   * entière ».* Il avait raison : l'écran ouvrait le référentiel entier — 2 410 lignes, vingt-huit
   * colonnes, un tableau qui défile — avec la fiche coincée au milieu. Sur un téléphone, devant un
   * rayonnage, ce n'est pas ce qu'on vient chercher.
   *
   * Un clic sur « voir tout le référentiel » quitte ce mode : l'écran d'administration reste à un
   * geste, il ne disparaît pas.
   */
  const [modeScan, setModeScan] = useState(codeInitial !== undefined);

  /**
   * Un filtre par colonne, en plus de la recherche (09/09/2026).
   *
   * Séparé de la recherche libre, qui vise le code ou le libellé : ce sont deux gestes
   * différents. « Chercher un tampon » se tape ; « voir tous les articles de nature DAF.020015 »
   * se choisit dans la colonne, parce qu'on ne connaît pas la valeur exacte de mémoire.
   */
  const [filtres, setFiltres] = useState<Filtres>({});

  /**
   * Quitter la page d'arrivée d'un scan, **en refermant la fiche**.
   *
   * Sans cela, la fiche reste ouverte au-dessus du tableau qui contient déjà la même ligne : deux
   * blocs pour un seul objet se lisent comme un doublon, et le bouton « fermer » ne fermait rien.
   */
  const quitterLeScan = (): void => {
    setModeScan(false);
    setOuvert(null);
  };
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [bilan, setBilan] = useState<BilanImportArticles | null>(null);
  const champFichier = useRef<HTMLInputElement>(null);

  const recharger = (): void => {
    chargerArticles()
      .then(({ articles, baseQr }) => {
        setEtat({ phase: "chargee", articles, baseQr });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  /**
   * Ouvrir la fiche de l'article que l'adresse demande, dès que la liste est là.
   *
   * Le code arrive de l'URL, donc avant les données : la fiche ne peut s'ouvrir qu'au retour du
   * chargement. Et un code inconnu ne fait rien d'autre que laisser la recherche vide de
   * résultats — ce qui dit la vérité, plutôt qu'un écran d'erreur pour une étiquette d'un article
   * supprimé depuis.
   */
  useEffect(() => {
    if (codeInitial === undefined || etat.phase !== "chargee") return;
    const trouve = etat.articles.find((a) => a.code === codeInitial);
    if (trouve !== undefined) setOuvert(trouve.id);
  }, [codeInitial, etat]);

  const agir = (id: string | null, action: Promise<unknown>): void => {
    setEnAttente(id);
    setErreur(null);
    action
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  const articles = etat.phase === "chargee" ? etat.articles : [];
  const baseQr = etat.phase === "chargee" ? etat.baseQr : "";

  /**
   * Les valeurs que chaque colonne porte, pour le filtre et la saisie assistée (09/09/2026).
   *
   * Calculées une fois pour toutes les colonnes : trente parcours de 2 411 articles à chaque
   * frappe rendraient la saisie collante, et c'est la même mémorisation que celle de la
   * recherche juste en dessous.
   */
  const propositions = useMemo(() => {
    const par = new Map<keyof Article, readonly Proposition[]>();
    for (const c of COLONNES_FICHIER) {
      if (c.genre === "booleen") continue;
      const liste = propositionsDeLaColonne(articles, c.champ);
      if (liste.length > 0) par.set(c.champ, liste);
    }
    return par;
  }, [articles]);

  /** Les colonnes dont le filtre est une liste déroulante : la valeur y est donc EXACTE. */
  const exacts = useMemo(() => {
    const set = new Set<keyof Article>();
    for (const c of COLONNES_FICHIER) {
      if (c.genre === "booleen") {
        set.add(c.champ);
        continue;
      }
      const liste = propositions.get(c.champ);
      if (liste !== undefined && liste.length <= MAX_LISTE_DEROULANTE) set.add(c.champ);
    }
    return set;
  }, [propositions]);

  // La recherche est mémorisée : sans cela, chaque frappe refiltrerait le référentiel entier à
  // chaque rendu du composant, y compris ceux que la saisie d'un autre champ déclenche.
  const trouves = useMemo(
    () => filtrer(chercherArticles(articles, recherche), filtres, exacts),
    [articles, recherche, filtres, exacts],
  );

  if (etat.phase === "chargement") return <p>Chargement des articles…</p>;
  if (etat.phase === "erreur") return <p className="alerte">{etat.raison}</p>;

  const affiches = trouves.slice(0, LIGNES_AFFICHEES);
  const article = articles.find((a) => a.id === ouvert);
  const articleDuQr = articles.find((a) => a.id === qrEnGrand);

  /**
   * LA PAGE D'ARRIVÉE D'UN SCAN : la fiche, seule.
   *
   * *« Ce QR code pourra servir dans d'autres menus que le menu stock, cela devra marcher
   * partout »* (Chris, 20/08/2026). C'est pourquoi l'adresse désigne l'ARTICLE et non une
   * fonction : `/article/<code>`, jamais `/stock/<code>`. La même étiquette servira donc la
   * saisie de stock, un inventaire, ou ce qui viendra après — chaque écran partira de cette page
   * plutôt que d'exiger son propre QR.
   */
  if (modeScan) {
    const scane = articles.find((a) => a.code === codeInitial);
    return (
      <section className="referentiel articles articles--scan">
        {scane === undefined ? (
          <div className="articles__amorce">
            <p>
              <strong>Aucun article ne porte le code « {codeInitial} ».</strong> L'étiquette
              désigne un article qui n'est plus au référentiel, ou qui n'y est pas encore entré.
            </p>
            <button
              type="button"
              className="lien"
              onClick={quitterLeScan}
            >
              Voir tout le référentiel
            </button>
          </div>
        ) : (
          <>
            {/* Pas de titre ici : la fiche porte déjà le code et le libellé. Le répéter au-dessus
                ferait deux fois la même ligne sur un écran de téléphone, où la place manque. */}
            <p className="etat">
              <button type="button" className="lien" onClick={quitterLeScan}>
                Voir tout le référentiel
              </button>
            </p>
            {erreur !== null && (
              <p className="alerte" role="alert">
                {erreur}
              </p>
            )}
            <Fiche
              article={scane}
              baseQr={baseQr}
              enAttente={enAttente === scane.id}
              onAgrandirQr={() => {
                setQrEnGrand(scane.id);
              }}
              onModifier={(champs) => {
                agir(scane.id, modifierArticle(scane.id, champs));
              }}
              onDeposerPhoto={(numero, fichier) => {
                agir(scane.id, deposerPhotoArticle(scane.id, numero, fichier));
              }}
              onRetirerPhoto={(numero) => {
                agir(scane.id, retirerPhotoArticle(scane.id, numero));
              }}
              onFermer={quitterLeScan}
            />
            {articleDuQr !== undefined && (
              <QrAgrandi
                article={articleDuQr}
                baseQr={baseQr}
                onFermer={() => {
                  setQrEnGrand(null);
                }}
              />
            )}
          </>
        )}
      </section>
    );
  }

  /** L'aller-retour par Excel et la planche, réunis : ils servent au même geste. */
  const outils = (
    <p className="etat">
      <a href={ADRESSE_CLASSEUR_ARTICLES} download>
        Exporter le classeur
      </a>
      {" · "}
      <button type="button" className="lien" onClick={() => champFichier.current?.click()}>
        Réimporter un classeur
      </button>
      {articles.length > 0 && (
        <>
          {" · "}
          <a href={adresseEtiquettesArticles()} target="_blank" rel="noreferrer">
            Imprimer toutes les étiquettes
          </a>
        </>
      )}
      <input
        ref={champFichier}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          e.target.value = "";
          if (fichier === undefined) return;
          setBilan(null);
          setErreur(null);
          importerArticles(fichier)
            .then((resultat) => {
              setBilan(resultat);
              recharger();
            })
            .catch((err: unknown) => {
              setErreur(err instanceof Error ? err.message : String(err));
            });
        }}
      />
    </p>
  );

  return (
    <section className="referentiel articles">
      <h2>
        Articles — {articles.length} au référentiel
        {recherche.trim() !== "" && ` · ${trouves.length} trouvé(s)`}
      </h2>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {/*
        **Le référentiel vide ne se dit pas dans une case de tableau.** Une ligne « aucun
        article » au milieu d'un en-tête de vingt-huit colonnes se lit comme un écran en panne,
        alors qu'il ne manque qu'un geste. Tant que rien n'est chargé, l'écran ne montre donc que
        ce geste.
      */}
      {articles.length === 0 ? (
        <div className="articles__amorce">
          <p>
            <strong>Le référentiel est vide.</strong> Déposer le classeur des articles exporté de
            l'ERP : ses colonnes sont reprises telles quelles, et une ligne sans code ou sans
            description est signalée sans faire échouer le reste.
          </p>
          {outils}
          {bilan !== null && <Bilan bilan={bilan} />}
          <p className="note">
            Un article se crée aussi à la main, si besoin d'un seul :
          </p>
          <Ajout
            onAjouter={(code, description) => {
              agir(
                null,
                creerArticle({ code, description }).then((cree) => {
                  setOuvert(cree.id);
                }),
              );
            }}
          />
        </div>
      ) : (
        <>
          <p className="note">
            Le référentiel des articles est distinct de celui des ressources génériques : celui-ci
            porte ce qu'on achète, celui-là ce qui produit des heures. Le rattachement à une ligne
            de contre-étude se fait par le <strong>code article</strong>, jamais par le libellé.
            Les colonnes sont celles du classeur de l'ERP, dans son ordre ; <strong>une cellule
            vidée ou mise à zéro vide le champ</strong>, ici comme au réimport.
          </p>

          <Ajout
            onAjouter={(code, description) => {
              agir(
                null,
                creerArticle({ code, description }).then((cree) => {
                  setOuvert(cree.id);
                }),
              );
            }}
          />

          {outils}
          {bilan !== null && <Bilan bilan={bilan} />}

          <p className="etat">
            <label>
              Chercher un code ou un libellé{" "}
              <input
                className="saisie saisie--texte"
                value={recherche}
                placeholder="1000001, réduction, tampon…"
                onChange={(e) => {
                  setRecherche(e.target.value);
                }}
              />
            </label>
          </p>

          {article !== undefined && (
            <Fiche
              article={article}
              baseQr={baseQr}
              enAttente={enAttente === article.id}
              onAgrandirQr={() => {
                setQrEnGrand(article.id);
              }}
              onModifier={(champs) => {
                agir(article.id, modifierArticle(article.id, champs));
              }}
              onDeposerPhoto={(numero, fichier) => {
                agir(article.id, deposerPhotoArticle(article.id, numero, fichier));
              }}
              onRetirerPhoto={(numero) => {
                agir(article.id, retirerPhotoArticle(article.id, numero));
              }}
              onFermer={() => {
                setOuvert(null);
              }}
            />
          )}

          {/*
            Le défilement horizontal est porté par ce cadre et non par la page : c'est ce qui
            permet à la colonne du code de rester collée à gauche pendant qu'on parcourt les
            vingt-sept autres.
          */}
          {/*
            LES VALEURS PROPOSÉES, écrites UNE fois pour tout le tableau.

            Un `datalist` par colonne, et non par cellule : cent lignes de trente colonnes en
            feraient trois mille, chacune portant jusqu'à 272 options. Le navigateur les partage
            par leur identifiant, et le filtre de la colonne s'en sert aussi.

            L'attribut `label` porte le libellé : Edge et Chrome l'affichent à côté de la valeur,
            et un navigateur qui l'ignore montre le code seul — jamais rien de faux.
          */}
          {[...propositions.entries()].map(([champ, liste]) => (
            <datalist key={champ} id={idDeLaListe(champ)}>
              {liste.map((p) => (
                <option
                  key={p.valeur}
                  value={p.valeur}
                  label={p.libelle === null ? undefined : p.libelle}
                />
              ))}
            </datalist>
          ))}

          <div className="articles__defilement">
            <table className="ressources articles__table">
              <ColonnesDuClasseur />
              <thead>
                <tr>
                  <th className="articles__code">N°</th>
                  {/*
                    LE STOCK DU MAGASIN, hors classeur (09/09/2026).

                    *« Pourquoi la quantité entrée en stock n'a pas été reprise ? »* Elle l'avait
                    été — mais la seule colonne de stock affichée était « Stocks », le reflet de
                    l'ERP, vide sur un article que l'application vient de créer. Celle qui bouge
                    est ici, en lecture seule : elle se recalcule depuis les mouvements.

                    Placée juste après le code et non dans l'ordre du classeur : elle n'en fait
                    pas partie, comme le QR et les photos.
                  */}
                  <th
                    className="col-nombre"
                    title="Stock tenu par le magasin : somme des entrées et sorties saisies à l'écran Stocks. Ne se modifie pas ici — il se recalcule depuis les mouvements."
                  >
                    Stock magasin
                  </th>
                  {COLONNES_FICHIER.slice(0, RANG_DU_QR).map((c) => (
                    <th key={c.champ} title={INFOBULLES[c.champ]}>
                      {c.entete}
                    </th>
                  ))}
                  <th>QR_code</th>
                  {COLONNES_FICHIER.slice(RANG_DU_QR).map((c) => (
                    <th key={c.champ} title={INFOBULLES[c.champ]}>
                      {c.entete}
                    </th>
                  ))}
                  <th>Photos</th>
                  <th />
                </tr>
                {/*
                  LA LIGNE DE FILTRES (09/09/2026).

                  Sous les en-têtes et non au-dessus du tableau : un filtre se pose sur la colonne
                  qu'on regarde, et une barre d'outils l'aurait éloigné de sa colonne dès qu'on
                  défile sur trente d'entre elles. Les filtres se croisent entre eux et avec la
                  recherche libre.
                */}
                <tr className="articles__filtres">
                  <th className="articles__code">
                    {Object.values(filtres).some((v) => v !== undefined && v !== "") && (
                      <button
                        type="button"
                        className="lien"
                        onClick={() => {
                          setFiltres({});
                        }}
                      >
                        Tout voir
                      </button>
                    )}
                  </th>
                  <th />
                  {COLONNES_FICHIER.slice(0, RANG_DU_QR).map((c) => (
                    <th key={c.champ}>
                      <FiltreDeColonne
                        champ={c.champ}
                        entete={c.entete}
                        genre={c.genre}
                        propositions={propositions.get(c.champ) ?? []}
                        valeur={filtres[c.champ] ?? ""}
                        onFiltrer={(v) => {
                          setFiltres((f) => ({ ...f, [c.champ]: v }));
                        }}
                      />
                    </th>
                  ))}
                  <th />
                  {COLONNES_FICHIER.slice(RANG_DU_QR).map((c) => (
                    <th key={c.champ}>
                      <FiltreDeColonne
                        champ={c.champ}
                        entete={c.entete}
                        genre={c.genre}
                        propositions={propositions.get(c.champ) ?? []}
                        valeur={filtres[c.champ] ?? ""}
                        onFiltrer={(v) => {
                          setFiltres((f) => ({ ...f, [c.champ]: v }));
                        }}
                      />
                    </th>
                  ))}
                  <th />
                  <th />
                </tr>
              </thead>
              <tbody>
                {affiches.map((a) => {
                  const cellule = (c: (typeof COLONNES_FICHIER)[number]): JSX.Element => (
                    <td
                      key={c.champ}
                      className={c.genre === "nombre" ? "col-nombre" : undefined}
                    >
                      <Cellule
                        article={a}
                        champ={c.champ}
                        genre={c.genre}
                        largeur={c.largeur}
                        enAttente={enAttente === a.id}
                        liste={propositions.has(c.champ) ? idDeLaListe(c.champ) : undefined}
                        onModifier={(champs) => {
                          agir(a.id, modifierArticle(a.id, champs));
                        }}
                      />
                    </td>
                  );

                  return (
                    <tr key={a.id} className={a.id === ouvert ? "articles__ouvert" : undefined}>
                      {/* Le code ne s'édite pas : il relie la fiche au classeur et aux lignes de
                          contre-étude. Il ouvre la fiche, où vivent le QR et les photos. */}
                      <th className="articles__code">
                        <button
                          type="button"
                          className="lien code"
                          onClick={() => {
                            setOuvert(a.id === ouvert ? null : a.id);
                          }}
                        >
                          {a.code}
                        </button>
                      </th>
                      {/* Le stock des mouvements, en lecture : le modifier ici donnerait un
                          chiffre que le mouvement suivant écraserait. Il se corrige à l'écran
                          Stocks, en posant le mouvement qui manque. */}
                      <td className="col-nombre">{a.stockMagasin}</td>
                      {COLONNES_FICHIER.slice(0, RANG_DU_QR).map(cellule)}
                      <td>
                        {/*
                          Le QR de la ligne l'AGRANDIT en plein écran, à la taille qu'une
                          douchette lit. Vingt-huit colonnes ne laissent pas la place à cent QR
                          scanables, et la fiche ne le montrait pas assez grand non plus : c'est
                          le calque qui répond, pas un agrandissement sur place.
                        */}
                        <button
                          type="button"
                          className="articles__qr"
                          title={`Agrandir le QR code de ${a.code} pour le scaner`}
                          onClick={() => {
                            setQrEnGrand(a.id);
                          }}
                        >
                          <img
                            src={adresseQrArticle(a.id, baseQr)}
                            alt={`QR code de ${a.code}`}
                            loading="lazy"
                          />
                        </button>
                      </td>
                      {COLONNES_FICHIER.slice(RANG_DU_QR).map(cellule)}
                      <td className="articles__photos">
                        {([1, 2] as const).map((numero) => (
                          <PhotoDeLigne
                            key={numero}
                            article={a}
                            numero={numero}
                            enAttente={enAttente === a.id}
                            onDeposer={(fichier) => {
                              agir(a.id, deposerPhotoArticle(a.id, numero, fichier));
                            }}
                          />
                        ))}
                      </td>
                      <td>
                        {/*
                          Pas de boîte de dialogue : rien en base ne DÉSIGNE un article — le code
                          vit en texte sur les lignes de déboursé —, donc la suppression n'emporte
                          que la fiche du référentiel.
                        */}
                        <button
                          type="button"
                          className="lien"
                          disabled={enAttente === a.id}
                          onClick={() => {
                            if (a.id === ouvert) setOuvert(null);
                            agir(a.id, supprimerArticle(a.id));
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {affiches.length === 0 && (
                  <tr>
                    <td colSpan={COLONNES_FICHIER.length + 5} className="vide">
                      Aucun article ne correspond à cette recherche.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/*
            L'article est RELU dans la liste plutôt que gardé dans l'état : après un dépôt de
            photo, la liste est rechargée, et un article figé au moment du clic afficherait encore
            l'ancienne fiche. S'il a disparu entre-temps, le calque ne s'ouvre pas — plutôt que
            d'afficher un QR au hasard.
          */}
          {articleDuQr !== undefined && (
            <QrAgrandi
              article={articleDuQr}
              baseQr={baseQr}
              onFermer={() => {
                setQrEnGrand(null);
              }}
            />
          )}

          {trouves.length > affiches.length && (
            <p className="note">
              {affiches.length} lignes affichées sur {trouves.length} : affiner la recherche pour
              voir les suivantes. L'export, lui, porte tout le référentiel.
            </p>
          )}
        </>
      )}
    </section>
  );
}
